<template>
    <div class="step-delete pro">
        <h2 class="title">
            {{ title }}
        </h2>
        <p class="info">
            {{ description }}
        </p>
        <p
            class="instruction"
        >
            Type in <span>“{{ deletePhrase }}"</span> below and click the button to confirm.
        </p>
        <div class="cancel-box">
            <input
                v-model="validationPhrase"
                type="text"
            >
            <update-subscription-handler
                v-slot:default="{
                    isSubscriptionUpdating,
                    decrementSubscriptionProducts,
                }"
                @subscription-updated="removeShow"
            >
                <base-submit-button
                    class="btn is-red delete-btn"
                    :disabled="isSubscriptionUpdating || isButtonDisabled"
                    :loading="isSubscriptionUpdating || isDeleting"
                    @click="cancelShow(decrementSubscriptionProducts)"
                >
                    {{ buttonLabel }}
                </base-submit-button>
            </update-subscription-handler>
        </div>
        <a
            href="#"
            class="cancel-link"
            @click.prevent="$emit('change-step-prev')"
        >
            No, I changed my mind!
        </a>
    </div>
</template>

<script>
import { filter } from 'lodash';
import { mapState, mapActions } from 'vuex';
import toasts from '@/mixins/toasts';
import UpdateSubscriptionHandler from '@/components/Dashboard/Settings/Billing/UpdateSubscriptionHandler';

export default {
    components: {
        UpdateSubscriptionHandler,
    },

    mixins: [toasts],

    props: {
        show: {
            type: Object,
            default: () => {},
        },
        priceIds: {
            type: Array,
            default: () => ([]),
        },
    },

    data: () => ({
        validationPhrase: '',
        isDeleting: false,
        subscriptionCancelled: false,
    }),

    computed: {
        ...mapState([
            'shows',
            'userSubscription',
            'currentSubscription',
        ]),

        /**
         * Gets the delete phrase.
         *
         * @returns {string} Delete phrase.
         */
        deletePhrase() {
            if (this.isCancelingTrial || this.isCancelingSubcription) {
                return 'CANCEL';
            }

            return 'DELETE';
        },

        /**
         * Determines whether user is canceling trial.
         *
         * @returns {boolean} Check result.
         */
        isCancelingTrial() {
            return !! this.currentSubscription?.trial_ends_at;
        },

        /**
         * Determines whether user is canceling subscription.
         *
         * @returns {boolean} Check result.
         */
        isCancelingSubcription() {
            const shows = this.shows ? Object.values(this.shows) : [];

            return 1 === shows.filter((show) => show.isPro).length;
        },

        /**
         * Gets the step title.
         *
         * @returns {string} Step title.
         */
        title() {
            if (this.isCancelingTrial) {
                return 'Canceling your trial?';
            } else if (this.isCancelingSubcription) {
                return 'Canceling your subscription?';
            }

            return 'Delete show';
        },

        /**
         * Gets the step description.
         *
         * @returns {string} Step description.
         */
        description() {
            if (this.isCancelingTrial) {
                return 'That\'s too bad — just to be safe, you will have full access for the remaining time left on your trial.';
            }

            return 'Are you sure you want to delete your Show? Your players will be deactivated from your website!';
        },

        /**
         * Gets the step button label.
         *
         * @returns {string} Step button label.
         */
        buttonLabel() {
            if (this.isCancelingTrial) {
                return 'Cancel My Trial';
            } else if (this.isCancelingSubcription) {
                return 'Cancel My Plan';
            }

            return 'Delete My Show';
        },

        /**
         * Returns list of pro shows.
         *
         * @returns {object[]} List of pro shows.
         */
        proShows() {
            return filter(this.shows, show => ! show.plan.isFree);
        },

        /**
         * Indicates whether submit button is disabled or not.
         *
         * @returns {boolean} Is submit button disabled or not?
         */
        isButtonDisabled() {
            return this.isDeleting ||
                this.deletePhrase !== this.validationPhrase;
        },
    },

    methods: {
        ...mapActions(['updateShow', 'deleteShow']),

        /**
         * Cancels show.
         *
         * @param  {Function} decrementHandler Decrement handler callback.
         */
        cancelShow(decrementHandler) {
            if (! this.show.plan.isFree) {
                decrementHandler(this.userSubscription?.dbId, this.priceIds);
            } else {
                this.removeShow();
            }
        },

        /**
         * Deletes show.
         */
        async removeShow() {
            this.isDeleting = true;

            try {
                // Mark show to be removed at the end of subscription when only
                // one pro show was left.
                if (this.isCancelingSubcription) {
                    await this.updateShow({
                        hash: this.show.hash,
                        newShowData: { onSubscriptionEnded: 'remove' },
                    });

                    // Also now with last pro show subscription should be marked
                    // as cancelled.
                    this.subscriptionCancelled = true;
                } else {
                    await this.deleteShow(this.show.hash);
                }
            } catch (e) {
                this.toastError({
                    message: {
                        title: "We couldn'\t update your subscription. Please contact us.",
                    },
                });
            }

            this.isDeleting = false;

            this.$emit('change-step-next', {
                subscriptionCancelled: this.subscriptionCancelled,
            });
        },
    },
};
</script>
